import { createContext, useContext, useCallback, useEffect, type ReactNode } from 'react';
import { SITE_LOCALE, LOCALE_ORIGINS, type Locale } from '@/lib/site-config';
import en from './en.json';
// Build-time alias (vite.config.ts) that resolves to THIS build's locale
// bundle. Each ccTLD is its own build, so shipping all four JSONs (~723 KB
// raw) just to read one of them was pure bundle weight — the alias ships
// only English (the fallback) plus the active locale.
import active from '@i18n-active';

export { LOCALES, type Locale } from '@/lib/site-config';

const activeStrings = active as Record<string, string>;
const enStrings = en as Record<string, string>;

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale = SITE_LOCALE;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      if (next === locale) return;
      // Cross-domain navigation: each locale lives on its own ccTLD.
      const path = typeof window !== 'undefined' ? window.location.pathname : '/';
      window.location.href = `${LOCALE_ORIGINS[next]}${path === '/' ? '' : path}`;
    },
    [locale],
  );

  const t = useCallback(
    (key: string) => activeStrings[key] ?? enStrings[key] ?? key,
    [],
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

export function useT() {
  return useLocale().t;
}
